import { useEffect, useRef } from 'react';
import {
  buildStreamUrl,
  SUPPORT_STANDALONE,
} from 'components/SupportChat/api/supportApi';

export interface SupportAttachment {
  id: string;
  kind: 'image' | 'file';
  name?: string;
  mime?: string;
  size?: number;
}

export interface SupportStreamEvent {
  id?: number;
  conversation_id?: number;
  sender?: string;
  body?: string;
  status?: string;
  attachment?: SupportAttachment;
}

interface UseSupportStreamParams {
  conversationId: number | null;
  visitorToken?: string;
  token?: string;
  onEvent: (evt: SupportStreamEvent) => void;
}

// Reconnect backoff bounds (ms).
const RECONNECT_MIN = 1000;
const RECONNECT_MAX = 15000;

// FOLLOW-UP: the api-key / visitor_token travel as SSE query params because
// EventSource cannot set headers — this leaks into server access logs. Needs a
// short-lived stream-scoped token design (human decision).

/**
 * Subscribes to the conversation SSE stream via native EventSource and, on a
 * dropped connection, reconnects with backoff — re-building the stream URL with
 * the last seen event id so the server replays only the missed operator/system
 * messages (Last-Event-ID recovery). If SSE is entirely unavailable the widget
 * still works (send is a plain POST) — this hook fails silently (US1).
 */
export const useSupportStream = ({
  conversationId,
  visitorToken,
  token,
  onEvent,
}: UseSupportStreamParams): void => {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const lastEventId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (conversationId == null) return;
    if (typeof window === 'undefined' || !('EventSource' in window)) return;

    let source: EventSource | null = null;
    let closed = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = RECONNECT_MIN;

    const handle = (e: MessageEvent) => {
      try {
        const data: SupportStreamEvent = JSON.parse(e.data);
        if (data?.id != null) lastEventId.current = data.id;
        backoff = RECONNECT_MIN; // healthy frame — reset backoff
        onEventRef.current(data);
      } catch {
        /* ignore malformed frames */
      }
    };

    const scheduleReconnect = () => {
      if (closed || retryTimer) return;
      const delay = backoff;
      backoff = Math.min(backoff * 2, RECONNECT_MAX);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (closed) return;
      // Rebuild the URL each attempt so a reconnect carries the latest
      // last_event_id and the server replays only what we missed.
      const url = buildStreamUrl({
        conversationId,
        visitorToken,
        token,
        lastEventId: lastEventId.current,
      });
      try {
        // Standalone service is a different origin: open with credentials so
        // the access_token cookie is sent for server-side identity.
        source = new EventSource(
          url,
          SUPPORT_STANDALONE ? { withCredentials: true } : undefined
        );
      } catch {
        scheduleReconnect(); // construction failed — retry with backoff
        return;
      }
      source.addEventListener('operator_message', handle as EventListener);
      source.addEventListener('system', handle as EventListener);
      source.addEventListener('status', handle as EventListener);
      source.onerror = () => {
        // Close the broken source and reconnect ourselves with backoff +
        // last_event_id (more robust than native auto-retry alone, and lets us
        // carry the cursor forward across reconnects).
        if (source) {
          source.close();
          source = null;
        }
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (source) {
        source.close();
        source = null;
      }
    };
  }, [conversationId, visitorToken, token]);
};
